// Ajustes > Ajuda e sobre > Como usar (texto 08): o GUIA do app.
// De cima para baixo: busca, "Gestos do app", "Primeiros passos", "Recursos que valem para todas as abas" (uma página cada),
// "Guia das abas" (mesmos grupos e nomes da tela Abas) e "Perguntas comuns". Tudo recolhido, com ▸/▾ à esquerda do título (Recolhivel).
// As páginas abrem dentro de Ajustes (pilha de páginas de app/(tabs)/ajustes.tsx), com ‹ para voltar.
import { useEffect, useState } from 'react';
import { Modal, Pressable, Text, View } from 'react-native';
import { C, ABAS, abasVisiveis, GRUPOS, aba, alpha, useCores } from '../../components/theme';
import { N_, t, useIdioma } from '../../lib/i18n';
import { Bloco, BotaoAcao, Divisor } from '../../components/ajustes/Linhas';
import { Field } from '../../components/ui';
import { Recolhivel } from '../../components/Recolhivel';
import { setAberto } from '../../lib/recolher';
import { useCaixas } from '../../lib/caixas';
import { DIAS_TESTE_ABA } from '../../lib/plano';
import { IconeAba } from '../../components/IconeAba';
import { BotaoMenu } from './comum';
import Paywall from '../Paywall';
import type { AbaId } from '../../store/types';
import { abaDaPagina, buscarNoGuia, conteudoGuia, descricaoAba, idAba, idRecurso, paginaDaAba, paginaGuia, type Achado } from '../../lib/guia/conteudo';
import { pedirFoco, useFoco } from '../../lib/guia/destaque';
import { AvisoConstrucao, ListaPerguntas, PaginaGuia, type Rolar } from '../../components/guia/Pagina';
import { abrirAba } from '../../components/guia/Destaque';

// Gestos de antes (0.67): aparecem enquanto o conteúdo novo do bloco "Gestos do app" não existir.
const GESTOS: [string, string, string][] = [
  ['👉', N_('Arrastar para a direita'), N_('Exclui o item (pede confirmação). Depois dá para desfazer ou restaurar pela Lixeira.')],
  ['👈', N_('Arrastar para a esquerda'), N_('Mostra Arquivar e Duplicar.')],
  ['✊', N_('Segurar e arrastar'), N_('Muda a ordem dos itens.')],
  ['☝️', N_('Segurar e soltar'), N_('Entra no modo seleção, para escolher vários itens de uma vez.')],
  ['⋮', N_('Menu ⋮'), N_('Todas as opções do item: editar, mover, compartilhar, arquivar, excluir...')],
  ['⏰', N_('Me avise em…'), N_('No menu ⋮ de qualquer item: o celular avisa na hora escolhida, sem criar tarefa no My Planner.')],
];
function GestosDeAntes() {
  return (
    <Bloco>
      {GESTOS.map(([ic, g, d], i) => (
        <View key={g}>
          {i > 0 && <Divisor />}
          <View style={{ flexDirection: 'row', gap: 12, paddingVertical: 10, alignItems: 'flex-start' }}>
            <Text style={{ fontSize: 20, width: 26, textAlign: 'center', color: C.text }}>{ic}</Text>
            <View style={{ flex: 1, gap: 2 }}>
              <Text style={{ color: C.text, fontWeight: '700' }}>{t(g)}</Text>
              <Text style={{ color: C.sub, fontSize: 12 }}>{t(d)}</Text>
            </View>
          </View>
        </View>
      ))}
    </Bloco>
  );
}

const BLOCOS_TOPO: Record<string, string> = { gestos: 'guia:bloco:gestos', primeiros: 'guia:bloco:primeiros', perguntas: 'guia:bloco:perguntas' };

/** Título da página do guia aberta em Ajustes (id sem o "guia:"). */
export function tituloGuia(id: string) {
  const ab = abaDaPagina(id);
  return ab ? aba(ab).nome : (paginaGuia(id)?.titulo || t('Como usar'));
}

/** A tela Como usar (o topo do guia). onAbrir: abre uma página dentro de Ajustes ('guia:<id>'). */
export default function ComoUsar({ onAbrir, rolar }: { onAbrir: (pagina: `guia:${string}`) => void; rolar?: Rolar }) {
  useIdioma();
  const { accent } = useCores();
  const c = conteudoGuia();
  const [busca, setBusca] = useState('');
  const foco = useFoco();
  // busca ou "Voltar ao guia" para um bloco desta tela: abre o bloco (a página de dentro abre a seção e rola)
  useEffect(() => {
    if (foco && BLOCOS_TOPO[foco.pagina]) setAberto(BLOCOS_TOPO[foco.pagina], true);
  }, [foco?.n]); // eslint-disable-line react-hooks/exhaustive-deps

  const achados = busca.trim() ? buscarNoGuia(busca) : null;
  const ir = (a: Achado) => {
    setBusca('');
    const pid = a.destino.pagina;
    if (BLOCOS_TOPO[pid]) setAberto(BLOCOS_TOPO[pid], true);
    else onAbrir(`guia:${pid}`);
    if (a.destino.secao || a.destino.passo || a.destino.pergunta) setTimeout(() => pedirFoco(a.destino), 60);
  };

  return (<>
    <Field value={busca} onChangeText={setBusca} placeholder={'🔍 ' + t('Buscar no guia')} autoCorrect={false} semDitado />
    {achados ? (<>
      {achados.map((a, i) => <BotaoMenu key={i} icon={a.icone ?? '❓'} titulo={a.texto} resumo={a.caminho} onPress={() => ir(a)} />)}
      {!achados.length && <Text style={{ color: C.sub, textAlign: 'center', marginTop: 12 }}>{t('Nada encontrado no guia.')}</Text>}
    </>) : (<>
      <Recolhivel visual="titulo" chave={BLOCOS_TOPO.gestos} inicial={false} titulo={t('Gestos do app')} gap={10}>
        {c.gestos.secoes.length ? <PaginaGuia pagina={c.gestos} cor={accent} rolar={rolar} tamGesto="m" /> : <GestosDeAntes />}
      </Recolhivel>

      <Recolhivel visual="titulo" chave={BLOCOS_TOPO.primeiros} inicial={false} titulo={t('Primeiros passos')} gap={10}>
        {c.primeirosPassos.secoes.length ? <PaginaGuia pagina={c.primeirosPassos} cor={accent} rolar={rolar} /> : <AvisoConstrucao />}
      </Recolhivel>

      <Recolhivel visual="titulo" chave="guia:bloco:recursos" inicial={false} titulo={t('Recursos que valem para todas as abas')} gap={10}>
        {c.recursos.map(r => <BotaoMenu key={r.id} icon={r.icone ?? '📘'} titulo={r.titulo} onPress={() => onAbrir(`guia:${idRecurso(r.id)}`)} />)}
        {!c.recursos.length && <AvisoConstrucao />}
      </Recolhivel>

      <Recolhivel visual="titulo" chave="guia:bloco:abas" inicial={false} titulo={t('Guia das abas')} gap={10}>
        <GuiaDasAbas onAbrir={id => onAbrir(`guia:${idAba(id)}`)} />
      </Recolhivel>

      <Recolhivel visual="titulo" chave={BLOCOS_TOPO.perguntas} inicial={false} titulo={t('Perguntas comuns')} gap={10}>
        <View style={{ backgroundColor: C.card, borderRadius: 14, paddingHorizontal: 14, paddingVertical: 6 }}>
          <ListaPerguntas pagina="perguntas" perguntas={c.perguntas} cor={accent} rolar={rolar} />
        </View>
      </Recolhivel>
    </>)}
  </>);
}

/** B1: as abas pelos mesmos grupos da tela Abas, cada grupo recolhido. TODAS as abas aparecem; as sem acesso com 🔒. */
function GuiaDasAbas({ onAbrir }: { onAbrir: (id: AbaId) => void }) {
  const { liberada, situacao, corDe } = useCores();
  const cx = useCaixas();
  // mesmo texto da lista de Ajustes > Abas (screens/ajustes/Abas.tsx)
  const resumoBloqueada = (id: AbaId) => (situacao(id).tipo === 'vencida' ? t('O teste grátis terminou') : t('Teste grátis por {n} dias', { n: DIAS_TESTE_ABA }));
  return (<>
    {GRUPOS.map(([g, nome]) => {
      const lista = abasVisiveis().filter(a => a.grupo === g);
      if (!lista.length) return null;
      return (
        <Recolhivel key={g} visual="cartao" chave={'guia:grupo:' + g} inicial={false} titulo={t(nome)} gap={8}>
          {lista.map(a => {
            const ok = liberada(a.id);
            return (
              <Pressable key={a.id} onPress={() => onAbrir(a.id)} accessibilityRole="button"
                style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: cx.gap(12), paddingVertical: cx.pad(10), paddingHorizontal: cx.padH(14),
                  borderRadius: cx.raio(14), backgroundColor: pressed ? C.card2 : C.card })}>
                <View style={{ width: 30, alignItems: 'center' }}><IconeAba id={a.id} icon={a.icon} tam={24} opacity={ok ? 1 : 0.5} /></View>
                <View style={{ flex: 1, gap: 2 }}>
                  <Text style={{ color: C.text, fontWeight: '700', fontSize: 15 }} numberOfLines={1}>{a.nome}</Text>
                  <Text style={{ color: C.sub, fontSize: 12 }} numberOfLines={1}>{descricaoAba(a.id)}</Text>
                  {!ok && <Text style={{ color: situacao(a.id).tipo === 'vencida' ? C.warn : C.ok, fontSize: 12, fontWeight: '700' }}>🔒 {resumoBloqueada(a.id)}</Text>}
                </View>
                {!ok && <Text>🔒</Text>}
                <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: ok ? corDe(a.id) : C.mute }} />
                <Text style={{ color: C.mute, fontSize: 22 }}>›</Text>
              </Pressable>
            );
          })}
        </Recolhivel>
      );
    })}
  </>);
}

/** Uma página do guia aberta dentro de Ajustes: recurso geral ou aba (modelo B2). id sem o "guia:". */
export function GuiaSubpagina({ id, rolar }: { id: string; rolar?: Rolar }) {
  useIdioma();
  const { accent } = useCores();
  const ab = abaDaPagina(id);
  if (ab) return <GuiaAba id={ab} rolar={rolar} />;
  const p = paginaGuia(id);
  if (!p) return <AvisoConstrucao />;
  return <PaginaGuia pagina={p} cor={accent} rolar={rolar} depois={!p.secoes.length ? <AvisoConstrucao /> : undefined} />;
}

/** B2: página de uma aba. Ícone e descrição, "Abrir esta aba" (sem acesso: explicação com 🔒), seções (só "Para que serve" aberta). */
function GuiaAba({ id, rolar }: { id: AbaId; rolar?: Rolar }) {
  const { liberada, situacao, corDe } = useCores();
  const [explicar, setExplicar] = useState(false);
  const a = aba(id);
  const ok = liberada(id);
  const cor = ok ? corDe(id) : C.sub;
  const { pagina, pronta } = paginaDaAba(id);
  // começou o teste ou assinou na explicação: fecha
  useEffect(() => { if (explicar && ok) setExplicar(false); }, [explicar, ok]);
  const topo = (<>
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 16, backgroundColor: alpha(ok ? cor : '#6B7280', 0.14) }}>
      <IconeAba id={id} icon={a.icon} tam={34} opacity={ok ? 1 : 0.6} />
      <View style={{ flex: 1, gap: 2 }}>
        <Text style={{ color: C.text, fontWeight: '800', fontSize: 17 }}>{a.nome}</Text>
        <Text style={{ color: C.sub, fontSize: 13 }}>{descricaoAba(id)}</Text>
        {!ok && <Text style={{ color: situacao(id).tipo === 'vencida' ? C.warn : C.ok, fontSize: 12, fontWeight: '700' }}>
          🔒 {situacao(id).tipo === 'vencida' ? t('O teste grátis terminou') : t('Teste grátis por {n} dias', { n: DIAS_TESTE_ABA })}</Text>}
      </View>
    </View>
    <BotaoAcao cheio cor={ok ? cor : corDe(id)} label={(ok ? '' : '🔒 ') + t('Abrir esta aba')} onPress={() => (ok ? abrirAba(id) : setExplicar(true))} />
  </>);
  return (<>
    <PaginaGuia pagina={pagina} cor={ok ? cor : corDe(id)} rolar={rolar} topo={topo}
      depois={!pronta ? <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 14 }}><AvisoConstrucao /></View> : undefined} />
    <Modal visible={explicar} animationType="slide" presentationStyle="fullScreen" onRequestClose={() => setExplicar(false)}>
      {explicar && <Paywall id={id} onBack={() => setExplicar(false)} />}
    </Modal>
  </>);
}
